<template>
  <div class="certificate-page">
    <van-pull-refresh v-model="refreshing" @refresh="onRefresh" success-text="刷新成功">
    <!-- 顶部区域 -->
    <div class="page-header">
      <div class="header-content">
        <h1 class="page-title">🏆 证书收集</h1>
        <p class="page-subtitle">已收集 {{ earnedCount }} / {{ allCertificates.length }} 个证书</p>
      </div>
    </div>

    <!-- 如何获取证书说明 -->
    <div class="how-to-get">
      <div class="how-to-header" @click="showHowTo = !showHowTo">
        <span class="how-to-icon">💡</span>
        <span class="how-to-title">如何获取证书？</span>
        <span class="how-to-arrow" :class="{ expanded: showHowTo }">›</span>
      </div>
      <div class="how-to-content" v-show="showHowTo">
        <div class="how-to-item">
          <span class="step-num">1</span>
          <div class="step-info">
            <p class="step-title">完成课程学习</p>
            <p class="step-desc">观看完整课程视频或学习资料</p>
          </div>
        </div>
        <div class="how-to-item">
          <span class="step-num">2</span>
          <div class="step-info">
            <p class="step-title">通过课程考试</p>
            <p class="step-desc">完成相关考试并达到合格分数</p>
          </div>
        </div>
        <div class="how-to-item">
          <span class="step-num">3</span>
          <div class="step-info">
            <p class="step-title">自动发放证书</p>
            <p class="step-desc">满足条件后系统自动颁发证书</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 证书收集卡片 -->
    <div class="certificate-container">
      <!-- 证书骨架屏 -->
      <div class="certificate-skeleton-grid" v-if="loading">
        <div class="cert-skeleton-card" v-for="i in 4" :key="i">
          <div class="skeleton-preview"></div>
          <div class="skeleton-line skeleton-name"></div>
          <div class="skeleton-line skeleton-status-line"></div>
        </div>
      </div>

      <!-- 证书卡片网格 -->
      <div v-else-if="allCertificates.length > 0" class="certificate-grid">
        <div
          class="cert-card"
          :class="{ 'cert-earned': item.IsEarned, 'cert-locked': !item.IsEarned }"
          v-for="(item, index) in allCertificates"
          :key="index"
          @click="onCertClick(item)"
        >
          <!-- 证书预览图 -->
          <div class="cert-preview" v-if="item.PreviewImage">
            <img :src="getImageUrl(item.PreviewImage)" :alt="item.Name" />
            <div class="preview-overlay" v-if="!item.IsEarned">
              <span class="lock-icon">🔒</span>
            </div>
          </div>

          <!-- 证书图标（无预览图时显示） -->
          <div class="cert-icon-wrapper" v-else>
            <div class="cert-icon" :class="{ 'icon-earned': item.IsEarned }">
              <span v-if="item.IsEarned">🏅</span>
              <span v-else>🔒</span>
            </div>
            <div class="cert-shine" v-if="item.IsEarned"></div>
          </div>

          <!-- 证书名称 -->
          <h4 class="cert-name">{{ item.Name }}</h4>

          <!-- 获取条件/状态 -->
          <p class="cert-status" v-if="item.IsEarned">
            <span class="status-earned">✓ 已获得</span>
          </p>
          <!-- 申请审核类: 待审核 -->
          <p class="cert-status" v-else-if="item.RuleType === 2 && item.ApplyStatus === 0" @click.stop="showRuleDialog(item)">
            <span class="status-pending">⏳ 审核中</span>
          </p>
          <!-- 申请审核类: 已驳回 -->
          <p class="cert-status" v-else-if="item.RuleType === 2 && item.ApplyStatus === 2" @click.stop="showRuleDialog(item)">
            <span class="status-rejected">❌ 已驳回</span>
          </p>
          <p class="cert-condition" v-else @click.stop="showRuleDialog(item)">
            <span class="acquire-type-hint" v-if="item.AcquireType === 'exam'">📝 考试获得</span>
            <span class="acquire-type-hint" v-else-if="item.RuleType === 2">📝 申请审核</span>
            <span class="acquire-type-hint" v-else-if="item.ConditionProgressList && item.ConditionProgressList.length > 0">
              {{ getOverallProgress(item) }}
            </span>
            <span class="view-detail">查看领取条件 ›</span>
          </p>

          <!-- 申请审核类: 立即申请按钮（CanApply 时显示） -->
          <div
            v-if="!item.IsEarned && item.RuleType === 2 && item.CanApply"
            class="cert-apply-wrap"
            @click.stop="openApplyDialog(item)"
          >
            <span class="cert-apply-btn">
              <span v-if="item.ApplyStatus === 2">🔄 重新申请</span>
              <span v-else>📝 立即申请</span>
            </span>
          </div>

          <!-- 获取时间 -->
          <p class="cert-time" v-if="item.IsEarned && item.CreateTime">
            {{ formatDate(item.CreateTime) }}
          </p>

          <!-- 实物证书物流入口 -->
          <div
            v-if="item.IsEarned && item.DeliveryStatus > 0"
            class="cert-delivery-wrap"
            @click.stop="goDelivery(item)"
          >
            <span
              class="cert-delivery-btn"
              :class="{
                'btn-pending': item.DeliveryStatus === 1,
                'btn-shipping': item.DeliveryStatus === 2 || item.DeliveryStatus === 3,
                'btn-received': item.DeliveryStatus === 4
              }"
            >
              <span v-if="item.DeliveryStatus === 1">📮 填写邮寄地址</span>
              <span v-else-if="item.DeliveryStatus === 2">📦 待发货</span>
              <span v-else-if="item.DeliveryStatus === 3">🚚 查看物流</span>
              <span v-else-if="item.DeliveryStatus === 4">✅ 已签收</span>
            </span>
          </div>
        </div>
      </div>

      <!-- 空状态 - 没有任何证书配置 -->
      <div v-if="!loading && allCertificates.length === 0" class="empty-state">
        <div class="empty-icon">🎯</div>
        <p class="empty-title">暂无证书</p>
        <p class="empty-desc">完成课程学习和考试后即可获得证书</p>
      </div>
    </div>

    <!-- 证书规则弹框 -->
    <van-dialog
      v-model="showRulePopup"
      title="证书获取条件"
      :show-confirm-button="false"
      :close-on-click-overlay="true"
      class="cert-rule-dialog"
      get-container="body"
      :lock-scroll="true"
    >
      <div class="rule-dialog-content" v-if="selectedCert">
        <!-- 锁图标 -->
        <div class="rule-lock-icon">🔐</div>

        <!-- 证书名称 -->
        <p class="rule-cert-name">{{ selectedCert.Name }}</p>

        <!-- 条件逻辑标签 -->
        <div class="rule-logic-tag" v-if="selectedCert.ConditionLogic">
          <span v-if="selectedCert.ConditionLogic === 'AND'">需满足全部条件</span>
          <span v-else>满足任一条件即可</span>
        </div>

        <!-- 条件列表（类似课程解锁的前置课程列表） -->
        <div class="rule-precourse-list" v-if="selectedCert.ConditionProgressList && selectedCert.ConditionProgressList.length > 0">
          <p class="rule-precourse-title">📋 完成情况：</p>
          <div class="rule-precourse-items">
            <template v-for="(prog, idx) in selectedCert.ConditionProgressList">
              <!-- 课程类型：可点击跳转 -->
              <div
                v-if="prog.TargetType === 'course'"
                :key="'prog-' + idx"
                class="rule-precourse-item"
                :class="{ 'completed': prog.IsSatisfied }"
                @click="goToCourse(prog)"
              >
                <span class="rule-status-dot" :class="prog.IsSatisfied ? 'dot-green' : 'dot-red'"></span>
                <span class="rule-precourse-name">{{ prog.Description }}</span>
                <span class="rule-precourse-action">
                  <span v-if="prog.IsSatisfied" class="action-done">✓ 已完成</span>
                  <span v-else class="action-go">去学习 ›</span>
                </span>
              </div>

              <!-- 考试类型：需要检测前置课程 -->
              <div
                v-else-if="prog.TargetType === 'exam'"
                :key="'prog-' + idx"
                class="rule-precourse-item"
                :class="{ 'completed': prog.IsSatisfied }"
                @click="goToExam(prog)"
              >
                <span class="rule-status-dot" :class="prog.IsSatisfied ? 'dot-green' : 'dot-red'"></span>
                <span class="rule-precourse-name">{{ prog.Description }}</span>
                <span class="rule-precourse-action">
                  <span v-if="prog.IsSatisfied" class="action-done">✓ 已通过</span>
                  <span v-else-if="prog.PreCourseCompleted === false" class="action-warn">需先完成课程</span>
                  <span v-else class="action-go">去考试 ›</span>
                </span>
              </div>

              <!-- 学习时长/课程数量：带进度条 -->
              <div
                v-else-if="prog.TargetType === 'hours' || prog.TargetType === 'count'"
                :key="'prog-' + idx"
                class="rule-precourse-item rule-progress-item-bar"
              >
                <div class="rule-progress-header">
                  <span class="rule-status-dot" :class="prog.IsSatisfied ? 'dot-green' : 'dot-red'"></span>
                  <span class="rule-precourse-name">{{ prog.Description }}</span>
                </div>
                <div class="rule-progress-bar-wrap">
                  <div class="rule-progress-bar-bg">
                    <div class="rule-progress-bar-fill" :style="{ width: prog.ProgressPercent + '%' }" :class="{ 'bar-done': prog.IsSatisfied }"></div>
                  </div>
                  <span class="rule-progress-text">{{ prog.ProgressText }}</span>
                </div>
              </div>

              <!-- 证书类型 -->
              <div
                v-else-if="prog.TargetType === 'certificate'"
                :key="'prog-' + idx"
                class="rule-precourse-item"
                :class="{ 'completed': prog.IsSatisfied }"
              >
                <span class="rule-status-dot" :class="prog.IsSatisfied ? 'dot-green' : 'dot-red'"></span>
                <span class="rule-precourse-name">{{ prog.Description }}</span>
                <span class="rule-precourse-action">
                  <span v-if="prog.IsSatisfied" class="action-done">✓ 已获得</span>
                  <span v-else class="action-pending">未获得</span>
                </span>
              </div>

              <!-- 默认类型 -->
              <div v-else :key="'prog-' + idx" class="rule-precourse-item">
                <span class="rule-status-dot" :class="prog.IsSatisfied ? 'dot-green' : 'dot-red'"></span>
                <span class="rule-precourse-name">{{ prog.Description }}</span>
                <span class="rule-precourse-action">
                  <span v-if="prog.IsSatisfied" class="action-done">✓</span>
                  <span v-else class="action-pending">{{ prog.ProgressText }}</span>
                </span>
              </div>
            </template>
          </div>
        </div>

        <!-- 无进度数据时的兜底显示 -->
        <div class="rule-precourse-list" v-else-if="selectedCert.RuleConditionDescs && selectedCert.RuleConditionDescs.length > 0">
          <p class="rule-precourse-title">📋 获取条件：</p>
          <div class="rule-precourse-items">
            <div class="rule-precourse-item" v-for="(desc, idx) in selectedCert.RuleConditionDescs" :key="idx">
              <span class="rule-status-dot dot-red"></span>
              <span class="rule-precourse-name">{{ desc }}</span>
            </div>
          </div>
        </div>

        <!-- 考试前置课程提示 -->
        <div class="rule-exam-tip" v-if="hasExamWithUnfinishedCourse">
          <span class="tip-icon">💡</span>
          <span class="tip-text">考试需先完成关联课程学习后才能参加</span>
        </div>

        <!-- 申请审核类: 申请进度提示 -->
        <div
          class="rule-apply-tip"
          v-if="selectedCert.RuleType === 2 && selectedCert.ApplyStatus === 0"
        >
          <span class="tip-icon">⏳</span>
          <span class="tip-text">申请已提交，请耐心等待管理员审核</span>
        </div>
        <div
          class="rule-apply-tip rule-apply-tip-error"
          v-if="selectedCert.RuleType === 2 && selectedCert.ApplyStatus === 2"
        >
          <span class="tip-icon">❌</span>
          <span class="tip-text">
            上次申请被驳回<span v-if="selectedCert.ApplyAuditRemark">：{{ selectedCert.ApplyAuditRemark }}</span>
          </span>
        </div>

        <p class="rule-hint" v-if="selectedCert.RuleType === 2">
          满足条件后可主动申请，由管理员审核通过后发放
        </p>
        <p class="rule-hint" v-else>完成上述条件后，证书将自动发放</p>

        <div class="rule-dialog-footer">
          <button class="btn-close" @click="showRulePopup = false">我知道了</button>
        </div>
      </div>
    </van-dialog>

    <!-- 申请提交弹框 -->
    <van-dialog
      v-model="showApplyPopup"
      title="提交证书申请"
      :show-confirm-button="false"
      :close-on-click-overlay="!applySubmitting"
      class="cert-apply-dialog"
      get-container="body"
      :lock-scroll="true"
    >
      <div class="apply-dialog-content" v-if="applyTarget">
        <p class="apply-cert-name">{{ applyTarget.Name }}</p>
        <p class="apply-tip">确认提交后由管理员审核，可在本页面查看进度</p>

        <!-- 备注（默认折叠，点击展开、选填） -->
        <div class="apply-remark-toggle" v-if="!showRemarkInput" @click="showRemarkInput = true">
          <span>➕ 补充备注（选填）</span>
        </div>
        <div class="apply-remark-block" v-else>
          <textarea
            v-model="applyRemark"
            class="apply-remark-input"
            placeholder="例如补充说明、联系方式等"
            maxlength="500"
            rows="3"
          ></textarea>
          <p class="apply-remark-counter">{{ applyRemark.length }} / 500</p>
        </div>

        <div class="apply-dialog-footer">
          <button
            class="btn-cancel"
            :disabled="applySubmitting"
            @click="showApplyPopup = false"
          >取消</button>
          <button
            class="btn-submit"
            :disabled="applySubmitting"
            @click="submitApply"
          >
            <span v-if="applySubmitting">提交中...</span>
            <span v-else>确认提交</span>
          </button>
        </div>
      </div>
    </van-dialog>
    </van-pull-refresh>

    <!-- 意见反馈按钮（放在 van-pull-refresh 外部，避免 transform 破坏 position:fixed） -->
    <!-- ref="feedbackFab" 配合 mounted/beforeDestroy 将节点搬到 document.body，
         避开 #app 上 will-change:transform 形成的新容器块，防止浮窗跟随页面滚动 -->
    <div
      ref="feedbackFab"
      class="feedback-fab"
      :class="{ 'feedback-fab-attract': fabAttract }"
      @click="openFeedbackDialog"
      @animationend="fabAttract = false"
    >
      <span class="feedback-fab-icon">💬</span>
      <span class="feedback-fab-text">意见反馈</span>
    </div>

    <!-- 意见反馈弹框 -->
    <van-dialog
      v-model="showFeedbackPopup"
      title="意见反馈"
      :show-confirm-button="false"
      :close-on-click-overlay="!feedbackSubmitting"
      class="feedback-dialog"
      get-container="body"
      :lock-scroll="true"
    >
      <div class="feedback-dialog-content">
        <p class="feedback-tip">您的每一条建议我们都认真对待</p>

        <div class="feedback-textarea-wrap">
          <textarea
            v-model="feedbackContent"
            class="feedback-textarea"
            placeholder="请输入您的意见或建议..."
            maxlength="500"
            rows="5"
          ></textarea>
          <p class="feedback-counter">{{ feedbackContent.length }} / 500</p>
        </div>

        <div class="feedback-checkbox-wrap" @click="feedbackAllowContact = !feedbackAllowContact">
          <span class="feedback-checkbox" :class="{ checked: feedbackAllowContact }">
            <span v-if="feedbackAllowContact" class="check-mark">✓</span>
          </span>
          <span class="feedback-checkbox-label">允许开发者主动联系我</span>
        </div>

        <div class="feedback-dialog-footer">
          <button
            class="btn-cancel"
            :disabled="feedbackSubmitting"
            @click="showFeedbackPopup = false"
          >取消</button>
          <button
            class="btn-submit"
            :disabled="!canSubmitFeedback || feedbackSubmitting"
            @click="submitFeedback"
          >
            <span v-if="feedbackSubmitting">提交中...</span>
            <span v-else>提交反馈</span>
          </button>
        </div>
      </div>
    </van-dialog>
  </div>
</template>

<script>
import {
  Certificate_GetAllCertificatesForCollection,
  CertificateApply_Submit,
  Feedback_Submit
} from '../api/api'

export default {
  name: 'certificate_list',
  data() {
    return {
      allCertificates: [], // 所有证书（包括已获得和未获得）
      loading: true,
      refreshing: false, // 下拉刷新状态
      showHowTo: false, // 默认收起说明
      showRulePopup: false, // 规则弹框
      selectedCert: null, // 选中的证书

      // 申请弹框相关
      showApplyPopup: false,
      applyTarget: null,
      applyRemark: '',
      showRemarkInput: false, // 备注输入默认折叠、点击才展开
      applySubmitting: false,

      // 意见反馈相关
      showFeedbackPopup: false,
      feedbackContent: '',
      feedbackAllowContact: false,
      feedbackSubmitting: false,
      fabAttract: false // 首次进入页面时，浮窗播放一次"点击"吸引效果
    }
  },
  computed: {
    earnedCount() {
      return this.allCertificates.filter(c => c.IsEarned).length
    },
    hasExamWithUnfinishedCourse() {
      if (!this.selectedCert || !this.selectedCert.ConditionProgressList) return false
      return this.selectedCert.ConditionProgressList.some(p => p.TargetType === 'exam' && p.PreCourseCompleted === false)
    },
    canSubmitFeedback() {
      return this.feedbackAllowContact && this.feedbackContent.trim().length > 0
    }
  },
  async mounted() {
    await this.loadCertificates()
    // 将意见反馈浮窗挂到 document.body 下，避免受 #app 的 will-change:transform
    // 影响（该属性会让 #app 变为 fixed 定位的新容器，导致浮窗跟随页面滚动）
    if (this.$refs.feedbackFab) {
      document.body.appendChild(this.$refs.feedbackFab)
    }
    // 首次进入本页面时，让浮窗自动播放一次"点击"缩放效果，吸引用户注意
    this.$nextTick(() => {
      setTimeout(() => {
        this.fabAttract = true
      }, 600)
    })
  },
  beforeDestroy() {
    // 组件销毁时移除挂到 body 下的浮窗节点，避免残留
    if (this.$refs.feedbackFab && this.$refs.feedbackFab.parentNode) {
      this.$refs.feedbackFab.parentNode.removeChild(this.$refs.feedbackFab)
    }
  },
  methods: {
    async loadCertificates() {
      this.loading = true
      try {
        const { data } = await Certificate_GetAllCertificatesForCollection()
        this.allCertificates = data.response || []
      } finally {
        this.loading = false
      }
    },
    async onRefresh() {
      try {
        const { data } = await Certificate_GetAllCertificatesForCollection()
        this.allCertificates = data.response || []
      } finally {
        this.refreshing = false
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    getImageUrl(path) {
      if (!path) return ''
      if (path.startsWith('http')) return path
      const baseUrl = 'https://share.51i.cc'
      return baseUrl + (path.startsWith('/') ? '' : '/') + path
    },
    onCertClick(item) {
      // 实物证书待填写地址时，优先引导到地址填写页
      if (item.IsEarned && item.DeliveryStatus === 1) {
        this.goDelivery(item)
        return
      }
      if (item.IsEarned && item.Path) {
        location.href = item.Path
      } else if (!item.IsEarned) {
        this.showRuleDialog(item)
      }
    },
    goDelivery(item) {
      if (!item || !item.MemberCertificateId) {
        this.$toast('证书信息异常')
        return
      }
      this.$router.push({
        path: '/certificate_delivery',
        query: { id: item.MemberCertificateId }
      })
    },
    showRuleDialog(item) {
      this.selectedCert = item
      this.showRulePopup = true
    },
    getOverallProgress(item) {
      if (!item.ConditionProgressList || item.ConditionProgressList.length === 0) return ''
      const satisfied = item.ConditionProgressList.filter(p => p.IsSatisfied).length
      const total = item.ConditionProgressList.length
      return `进度 ${satisfied}/${total}`
    },
    goToCourse(prog) {
      if (prog.IsSatisfied) return
      this.showRulePopup = false
      this.$router.push({ path: '/classStudy', query: { ids: prog.TargetId } })
    },
    goToExam(prog) {
      if (prog.IsSatisfied) return
      // 如果考试有前置课程且前置课程未完成，跳转到前置课程
      if (prog.PreCourseCompleted === false && prog.PreCourseId) {
        this.showRulePopup = false
        this.$toast('需先完成课程学习')
        this.$router.push({ path: '/classStudy', query: { ids: prog.PreCourseId } })
        return
      }
      // 前置课程已完成或无前置课程，跳转到考试列表
      this.showRulePopup = false
      this.$router.push({ path: '/examination_list' })
    },
    openApplyDialog(item) {
      if (!item) return
      if (!item.CanApply) {
        this.$toast('当前不满足申请条件')
        return
      }
      this.applyTarget = item
      this.applyRemark = ''
      this.showRemarkInput = false
      this.showRulePopup = false
      this.showApplyPopup = true
    },
    async submitApply() {
      if (!this.applyTarget) return
      if (this.applySubmitting) return
      this.applySubmitting = true
      try {
        const { data } = await CertificateApply_Submit({
          CertificateId: this.applyTarget.Id,
          ApplyRemark: this.applyRemark
        })
        if (data && data.success) {
          this.$toast.success(data.msg || '申请已提交')
          this.showApplyPopup = false
          await this.loadCertificates()
        } else {
          this.$toast.fail((data && data.msg) || '提交失败')
        }
      } catch (e) {
        this.$toast.fail('网络异常，请稍后再试')
      } finally {
        this.applySubmitting = false
      }
    },
    openFeedbackDialog() {
      this.feedbackContent = ''
      this.feedbackAllowContact = false
      this.feedbackSubmitting = false
      this.showFeedbackPopup = true
    },
    async submitFeedback() {
      if (!this.canSubmitFeedback) return
      if (this.feedbackSubmitting) return
      this.feedbackSubmitting = true
      try {
        const openid = localStorage.getItem('openid')
        const { data } = await Feedback_Submit({
          OpenId: openid,
          Content: this.feedbackContent.trim(),
          AllowContact: this.feedbackAllowContact
        })
        if (data && data.success) {
          this.$toast.success(data.msg || '反馈已提交')
          this.showFeedbackPopup = false
        } else {
          this.$toast.fail((data && data.msg) || '提交失败')
        }
      } catch (e) {
        this.$toast.fail('网络异常，请稍后再试')
      } finally {
        this.feedbackSubmitting = false
      }
    }
  }
}
</script>

<style scoped>
.certificate-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #fff8f6 0%, #f5f9fc 30%);
  -webkit-overflow-scrolling: touch;
}

/* 顶部区域 */
.page-header {
  background: linear-gradient(135deg, #d51900 0%, #ff6b5b 100%);
  padding: 24px 20px 20px;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.page-header::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -20%;
  width: 200px;
  height: 200px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
}

.page-header::after {
  content: '';
  position: absolute;
  bottom: -30%;
  left: -10%;
  width: 150px;
  height: 150px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 50%;
}

.header-content {
  position: relative;
  z-index: 1;
}

.page-title {
  font-size: 22px;
  font-weight: 800;
  color: #fff;
  margin: 0 0 6px;
  letter-spacing: 1px;
}

.page-subtitle {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
}

/* 如何获取证书说明 */
.how-to-get {
  margin: 16px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.how-to-header {
  display: flex;
  align-items: center;
  padding: 16px;
  cursor: pointer;
}

.how-to-header:active {
  background: #f9f9f9;
}

.how-to-icon {
  font-size: 20px;
  margin-right: 10px;
}

.how-to-title {
  flex: 1;
  font-size: 15px;
  font-weight: 600;
  color: #1a2a3a;
}

.how-to-arrow {
  font-size: 20px;
  color: #999;
  transition: transform 0.3s ease;
  transform: rotate(90deg);
}

.how-to-arrow.expanded {
  transform: rotate(-90deg);
}

.how-to-content {
  padding: 0 16px 16px;
  border-top: 1px solid #f0f4f8;
}

.how-to-item {
  display: flex;
  align-items: flex-start;
  padding: 14px 0;
  border-bottom: 1px dashed #f0f4f8;
}

.how-to-item:last-child {
  border-bottom: none;
}

.step-num {
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, #d51900 0%, #ff6b5b 100%);
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  margin-right: 12px;
  flex-shrink: 0;
}

.step-info {
  flex: 1;
}

.step-title {
  font-size: 14px;
  font-weight: 600;
  color: #1a2a3a;
  margin: 0 0 4px;
}

.step-desc {
  font-size: 12px;
  color: #8a9aaa;
  margin: 0;
}

/* 证书容器 */
.certificate-container {
  padding: 0 16px 30px;
}

/* 证书卡片网格 */
.certificate-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

/* 证书骨架屏 */
.certificate-skeleton-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.cert-skeleton-card {
  background: #fff;
  border-radius: 16px;
  padding: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}

.skeleton-preview {
  width: 100%;
  height: 120px;
  border-radius: 12px;
  margin-bottom: 12px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
}

.skeleton-line {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-name {
  height: 16px;
  width: 80%;
  margin: 0 auto 10px;
}

.skeleton-status-line {
  height: 24px;
  width: 60%;
  margin: 0 auto;
  border-radius: 12px;
}

@keyframes skeleton-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* 证书卡片 */
.cert-card {
  background: #fff;
  border-radius: 16px;
  padding: 12px;
  text-align: center;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.cert-card.cert-earned {
  background: linear-gradient(135deg, #fffbf0 0%, #fff5e6 100%);
  border: 2px solid #ffd700;
}

.cert-card.cert-locked {
  background: #f8f9fa;
  border: 2px dashed #e0e0e0;
}

.cert-card.cert-earned:active {
  transform: scale(0.98);
}

/* 证书预览图 */
.cert-preview {
  position: relative;
  width: 100%;
  height: 120px;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 12px;
  background: #f0f2f5;
}

.cert-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.preview-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
}

.lock-icon {
  font-size: 32px;
}

/* 证书图标 */
.cert-icon-wrapper {
  position: relative;
  width: 64px;
  height: 64px;
  margin: 0 auto 12px;
}

.cert-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  background: #f0f2f5;
  position: relative;
  z-index: 1;
}

.cert-icon.icon-earned {
  background: linear-gradient(135deg, #ffd700 0%, #ffb800 100%);
  box-shadow: 0 4px 15px rgba(255, 215, 0, 0.4);
}

/* 闪光效果 */
.cert-shine {
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  border-radius: 50%;
  background: linear-gradient(135deg, transparent 40%, rgba(255, 255, 255, 0.8) 50%, transparent 60%);
  animation: shine 2s infinite;
}

@keyframes shine {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 证书名称 */
.cert-name {
  font-size: 14px;
  font-weight: 600;
  color: #1a2a3a;
  margin: 0 0 8px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 40px;
}

.cert-locked .cert-name {
  color: #999;
}

/* 证书状态 */
.cert-status {
  margin: 0;
}

.status-earned {
  font-size: 13px;
  font-weight: 600;
  color: #52c41a;
  background: rgba(82, 196, 26, 0.1);
  padding: 4px 12px;
  border-radius: 12px;
}

.cert-condition {
  font-size: 11px;
  color: #999;
  margin: 0;
  line-height: 1.4;
  cursor: pointer;
}

.condition-text {
  display: block;
  margin-bottom: 4px;
}

.view-detail {
  color: #d51900;
  font-weight: 600;
}

.cert-time {
  font-size: 11px;
  color: #8a9aaa;
  margin: 6px 0 0;
}

/* 实物证书物流入口按钮 */
.cert-delivery-wrap {
  margin-top: 8px;
}

.cert-delivery-btn {
  display: inline-block;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 12px;
  line-height: 1.4;
}

.cert-delivery-btn.btn-pending {
  color: #fff;
  background: linear-gradient(135deg, #ff9a44 0%, #d51900 100%);
  box-shadow: 0 2px 6px rgba(213, 25, 0, 0.25);
  animation: cert-pulse-btn 1.8s infinite;
}

.cert-delivery-btn.btn-shipping {
  color: #1e6cff;
  background: rgba(30, 108, 255, 0.08);
}

.cert-delivery-btn.btn-received {
  color: #52c41a;
  background: rgba(82, 196, 26, 0.1);
}

@keyframes cert-pulse-btn {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.04); }
}

/* 空状态 */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.empty-title {
  font-size: 18px;
  font-weight: 600;
  color: #1a2a3a;
  margin: 0 0 8px;
}

.empty-desc {
  font-size: 14px;
  color: #8a9aaa;
  margin: 0;
}

/* 规则弹框样式 */
.rule-dialog-content {
  padding: 20px 16px 24px;
  text-align: center;
  max-height: 60vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.rule-lock-icon {
  font-size: 48px;
  margin-bottom: 12px;
}

.rule-cert-name {
  font-size: 16px;
  color: #333;
  margin: 0 0 6px;
  font-weight: 600;
}

.rule-logic-tag {
  text-align: center;
  margin-bottom: 12px;
}

.rule-logic-tag span {
  display: inline-block;
  padding: 4px 14px;
  background: #f0f4ff;
  color: #4a6cf7;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

/* 前置条件列表 */
.rule-precourse-list {
  background: linear-gradient(135deg, #f8fafc 0%, #f0f4f8 100%);
  border-radius: 16px;
  padding: 14px;
  text-align: left;
  margin-bottom: 12px;
}

.rule-precourse-title {
  font-size: 14px;
  color: #5a6a7a;
  margin: 0 0 10px;
  font-weight: 600;
}

.rule-precourse-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.rule-precourse-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: #fff;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
}

.rule-precourse-item:active {
  transform: scale(0.98);
  background: #f5f7fa;
}

.rule-precourse-item.completed {
  opacity: 0.7;
}

.rule-precourse-item.rule-progress-item-bar {
  flex-direction: column;
  align-items: stretch;
  cursor: default;
}

.rule-precourse-item.rule-progress-item-bar:active {
  transform: none;
  background: #fff;
}

.rule-progress-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

/* 呼吸灯效果 */
.rule-status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.rule-status-dot.dot-red {
  background: #ff4d4f;
  box-shadow: 0 0 0 0 rgba(255, 77, 79, 0.7);
  animation: cert-pulse-red 1.5s infinite;
}

.rule-status-dot.dot-green {
  background: #52c41a;
  box-shadow: 0 0 0 0 rgba(82, 196, 26, 0.7);
  animation: cert-pulse-green 2s infinite;
}

@keyframes cert-pulse-red {
  0% { box-shadow: 0 0 0 0 rgba(255, 77, 79, 0.7); }
  70% { box-shadow: 0 0 0 8px rgba(255, 77, 79, 0); }
  100% { box-shadow: 0 0 0 0 rgba(255, 77, 79, 0); }
}

@keyframes cert-pulse-green {
  0% { box-shadow: 0 0 0 0 rgba(82, 196, 26, 0.7); }
  70% { box-shadow: 0 0 0 8px rgba(82, 196, 26, 0); }
  100% { box-shadow: 0 0 0 0 rgba(82, 196, 26, 0); }
}

.rule-precourse-name {
  flex: 1;
  font-size: 14px;
  color: #333;
  font-weight: 500;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.rule-precourse-action {
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
}

.action-go {
  color: #d51900;
}

.action-done {
  color: #52c41a;
}

.action-warn {
  color: #fa8c16;
  font-size: 11px;
}

.action-pending {
  color: #999;
}

/* 进度条 */
.rule-progress-bar-wrap {
  display: flex;
  align-items: center;
  margin-left: 20px;
}

.rule-progress-bar-bg {
  flex: 1;
  height: 8px;
  background: #e8e8e8;
  border-radius: 4px;
  overflow: hidden;
  margin-right: 10px;
}

.rule-progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #ff9a44 0%, #d51900 100%);
  border-radius: 4px;
  transition: width 0.5s ease;
}

.rule-progress-bar-fill.bar-done {
  background: linear-gradient(90deg, #52c41a 0%, #389e0d 100%);
}

.rule-progress-text {
  font-size: 12px;
  color: #666;
  white-space: nowrap;
  min-width: 80px;
  text-align: right;
}

/* 考试前置课程提示 */
.rule-exam-tip {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  background: linear-gradient(135deg, #fffbe6 0%, #fff8e1 100%);
  border-radius: 12px;
  margin-bottom: 12px;
  text-align: left;
}

.tip-icon {
  font-size: 16px;
  flex-shrink: 0;
}

.tip-text {
  font-size: 12px;
  color: #8a6d3b;
  line-height: 1.4;
}

.rule-hint {
  font-size: 12px;
  color: #8a9aaa;
  margin: 0 0 16px;
}

.rule-dialog-footer {
  text-align: center;
}

.btn-close {
  width: 100%;
  padding: 14px;
  background: #d51900;
  color: #fff;
  border: none;
  border-radius: 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
}

.btn-close:active {
  opacity: 0.8;
}

/* 获取方式标签 */
.acquire-type-hint {
  display: block;
  font-size: 11px;
  color: #666;
  margin-bottom: 4px;
}

/* 申请审核状态标签 */
.status-pending {
  font-size: 13px;
  font-weight: 600;
  color: #fa8c16;
  background: rgba(250, 140, 22, 0.1);
  padding: 4px 12px;
  border-radius: 12px;
}

.status-rejected {
  font-size: 13px;
  font-weight: 600;
  color: #ff4d4f;
  background: rgba(255, 77, 79, 0.1);
  padding: 4px 12px;
  border-radius: 12px;
}

/* 立即申请按钮 */
.cert-apply-wrap {
  margin-top: 8px;
}

.cert-apply-btn {
  display: inline-block;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 14px;
  color: #fff;
  background: linear-gradient(135deg, #ff9a44 0%, #d51900 100%);
  box-shadow: 0 2px 6px rgba(213, 25, 0, 0.25);
  animation: cert-pulse-btn 1.8s infinite;
}

/* 规则弹框: 申请进度提示 */
.rule-apply-tip {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  background: linear-gradient(135deg, #fffbe6 0%, #fff8e1 100%);
  border-radius: 12px;
  margin-bottom: 12px;
  text-align: left;
}

.rule-apply-tip.rule-apply-tip-error {
  background: linear-gradient(135deg, #fff1f0 0%, #ffe7e6 100%);
}

.rule-apply-tip .tip-text {
  font-size: 12px;
  color: #8a6d3b;
  line-height: 1.4;
}

.rule-apply-tip-error .tip-text {
  color: #cf1322;
}

/* 申请弹框 */
.apply-dialog-content {
  padding: 20px 16px 16px;
}

.apply-cert-name {
  font-size: 16px;
  color: #333;
  margin: 0 0 6px;
  font-weight: 600;
  text-align: center;
}

.apply-tip {
  font-size: 12px;
  color: #8a9aaa;
  margin: 0 0 14px;
  text-align: center;
  line-height: 1.5;
}

.apply-remark-toggle {
  text-align: center;
  margin: 0 0 14px;
  cursor: pointer;
}

.apply-remark-toggle span {
  display: inline-block;
  font-size: 12px;
  color: #d51900;
  padding: 6px 14px;
  background: rgba(213, 25, 0, 0.06);
  border-radius: 12px;
}

.apply-remark-toggle:active span {
  background: rgba(213, 25, 0, 0.12);
}

.apply-remark-block {
  margin-bottom: 14px;
}

.apply-remark-input {
  width: 100%;
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 14px;
  color: #333;
  background: #f8fafc;
  resize: none;
  outline: none;
  font-family: inherit;
  box-sizing: border-box;
}

.apply-remark-input:focus {
  border-color: #d51900;
  background: #fff;
}

.apply-remark-counter {
  font-size: 11px;
  color: #999;
  text-align: right;
  margin: 4px 0 0;
}

.apply-dialog-footer {
  display: flex;
  gap: 10px;
}

.apply-dialog-footer .btn-cancel,
.apply-dialog-footer .btn-submit {
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 22px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}

.apply-dialog-footer .btn-cancel {
  background: #f0f2f5;
  color: #5a6a7a;
}

.apply-dialog-footer .btn-submit {
  background: linear-gradient(135deg, #ff9a44 0%, #d51900 100%);
  color: #fff;
  box-shadow: 0 4px 12px rgba(213, 25, 0, 0.3);
}

.apply-dialog-footer .btn-cancel:disabled,
.apply-dialog-footer .btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 意见反馈浮动按钮 */
.feedback-fab {
  position: fixed;
  right: 16px;
  bottom: 80px;
  z-index: 100;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: linear-gradient(135deg, #d51900 0%, #ff6b5b 100%);
  color: #fff;
  border-radius: 24px;
  box-shadow: 0 4px 16px rgba(213, 25, 0, 0.35);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.feedback-fab:active {
  transform: scale(0.95);
  opacity: 0.9;
}

/* 首次进入页面时，模拟一次点击缩放效果吸引用户注意 */
.feedback-fab-attract {
  animation: feedback-fab-click-attract 0.6s ease-in-out 2;
}

@keyframes feedback-fab-click-attract {
  0% { transform: scale(1); }
  50% { transform: scale(0.9); }
  100% { transform: scale(1); }
}

.feedback-fab-icon {
  font-size: 18px;
}

/* 意见反馈弹框 */
.feedback-dialog-content {
  padding: 20px 16px 16px;
}

.feedback-tip {
  font-size: 12px;
  color: #8a9aaa;
  margin: 0 0 14px;
  text-align: center;
  line-height: 1.5;
}

.feedback-textarea-wrap {
  margin-bottom: 14px;
}

.feedback-textarea {
  width: 100%;
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 14px;
  color: #333;
  background: #f8fafc;
  resize: none;
  outline: none;
  font-family: inherit;
  box-sizing: border-box;
  line-height: 1.6;
}

.feedback-textarea:focus {
  border-color: #d51900;
  background: #fff;
}

.feedback-counter {
  font-size: 11px;
  color: #999;
  text-align: right;
  margin: 4px 0 0;
}

/* 勾选框 */
.feedback-checkbox-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  cursor: pointer;
  user-select: none;
}

.feedback-checkbox {
  width: 20px;
  height: 20px;
  border: 2px solid #d0d0d0;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.feedback-checkbox.checked {
  background: linear-gradient(135deg, #d51900 0%, #ff6b5b 100%);
  border-color: #d51900;
}

.check-mark {
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
}

.feedback-checkbox-label {
  font-size: 14px;
  color: #333;
  line-height: 1.4;
}

/* 反馈弹框按钮复用申请弹框样式 */
.feedback-dialog-footer {
  display: flex;
  gap: 10px;
}

.feedback-dialog-footer .btn-cancel,
.feedback-dialog-footer .btn-submit {
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 22px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}

.feedback-dialog-footer .btn-cancel {
  background: #f0f2f5;
  color: #5a6a7a;
}

.feedback-dialog-footer .btn-submit {
  background: linear-gradient(135deg, #d51900 0%, #ff6b5b 100%);
  color: #fff;
  box-shadow: 0 4px 12px rgba(213, 25, 0, 0.3);
}

.feedback-dialog-footer .btn-cancel:disabled,
.feedback-dialog-footer .btn-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  box-shadow: none;
}
</style>



// WEBPACK FOOTER //
// src/views/certificate_list.vue